import { gql } from '@apollo/client';
import {
  PhoneNumberPreferences,
  SmsCountryPreferences,
  SmsCountryPreferencesVersion,
} from 'modules/graphql';
import { useGraphql } from 'utils/hooks/useGraphql';

export const getSmsPreferencesByCountry = gql`
  query getSmsPreferences {
    smsPreferencesByCountry {
      regionCode
      regionName
      numericOptOut
      alphanumericOptOut
      frequency
      updatedAt
      updatedBy {
        id
        fullName
        avatarUrl
      }
    }
  }
`;

export const getSmsPreferencesCountryVersionHistory = gql`
  query getCountryVersionHistory($regionCode: String!) {
    smsPreferencesCountryVersionHistory(country: $regionCode) {
      numericOptOut
      alphanumericOptOut
      frequency
      createdAt
      createdBy {
        id
        fullName
        avatarUrl
      }
    }
  }
`;

export const getSmsPreferencesForPhoneNumber = gql`
  query getPreferencesForPhoneNumber($phoneNumber: String!) {
    smsPreferencesForPhoneNumber(phoneNumber: $phoneNumber) {
      regionCode
      regionName
      numericOptOut
      alphanumericOptOut
    }
  }
`;

/** Get a list of all supported countries and their opt out settings */
export const useQuerySmsPreferencesByCountry = () => {
  const { data, errorMessage, isLoading, refetch } = useGraphql<{
    smsPreferencesByCountry: SmsCountryPreferences[];
  }>(getSmsPreferencesByCountry);

  return {
    errorMessage,
    isLoading,
    refetch,
    results: data?.smsPreferencesByCountry ?? [],
  };
};

/** Get version history for SMS opt-out settings for a specific country */
export const useQuerySmsPreferencesCountryVersionHistory = (regionCode: string) => {
  const { data, errorMessage, isLoading, refetch } = useGraphql<{
    smsPreferencesCountryVersionHistory: SmsCountryPreferencesVersion[];
  }>(getSmsPreferencesCountryVersionHistory, { variables: { regionCode } });

  return {
    errorMessage,
    isLoading,
    refetch,
    results: data?.smsPreferencesCountryVersionHistory ?? [],
  };
};

/** Get SMS preferences for the supplied phone number */
export const useQuerySmsPreferencesForPhoneNumber = (phoneNumber: string) => {
  const { data, errorMessage, isLoading, refetch } = useGraphql<{
    smsPreferencesForPhoneNumber: PhoneNumberPreferences;
  }>(getSmsPreferencesForPhoneNumber, { variables: { phoneNumber } });

  return {
    errorMessage,
    isLoading,
    refetch,
    results: data?.smsPreferencesForPhoneNumber ?? [],
  };
};
